/* global FormData */

import React from 'react'
import PropTypes from 'prop-types'
import { unionWith, eqBy, prop, pluck, remove, any, has, union } from 'ramda'
import MediaUploadModal from './media-upload'
import * as request from 'services/request'
import validate, { validateFile } from './validation'
import createProgress from './progress'
import notify from 'utils/notification'
import { addDuration } from './duration'
import * as reduxForm from 'redux-form'
import { connect } from 'react-redux'

const upload = (files, actionType, ownerId) => {
  if (!ownerId) {
    throw new Error('Please select an experience owner')
  }
  const form = new FormData()
  files.forEach(file => {
    form.append('media', file, file.name)
  })

  return request
    .authorized('/media', {
      method: 'POST',
      headers: {},
      body: form
    })
    .then(async response => {
      const mediaSvcResponse = await response.json()
      if (actionType !== 'MOSAIC_OPT_IN') {
        await Promise.all(
          mediaSvcResponse.map(async ({ id, originalName }) => {
            try {
              await request.authorized(
                `/media/validate?mediaId=${id}&destination=tweet&ownerId=${ownerId}`,
                {
                  method: 'POST',
                  headers: {}
                }
              )
            } catch (e) {
              const errorMessage = e.message + ` - ${originalName}`
              throw new Error(errorMessage)
            }
          })
        )
      }

      return mediaSvcResponse
    })
}

// how frequently the progress bar pretends to make progress
const updateMs = 50
const progress = createProgress(updateMs)

class MediaUploadModalContainer extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      files: [],
      error: '',
      dropzoneActive: false
    }

    this.handleMouseEnter = this.handleMouseEnter.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.handleDrop = this.handleDrop.bind(this)
    this.handleRemove = this.handleRemove.bind(this)
    this.handleSave = this.handleSave.bind(this)
    this.incFakeProgress = this.incFakeProgress.bind(this)
    this.handleHide = this.handleHide.bind(this)
  }

  handleMouseEnter () {
    this.setState({
      dropzoneActive: true
    })
  }

  handleMouseLeave () {
    this.setState({
      dropzoneActive: false
    })
  }

  /**
   * BEWARE: Here be dragons
   * Blue Robot caches the media to do some processing on it before uploading it to the cloud
   * This makes the XHR onprogress event inaccurate,
   * since it only measures the progress of the browser's upload.
   * Therefore this code implements a best-guess fake uploader
   *
   *  *                 ..:::::::::..
                    ..:::aad8888888baa:::..
                  .::::d:?88888888888?::8b::::.
                .:::d8888:?88888888??a888888b:::.
              .:::d8888888a8888888aa8888888888b:::.
            ::::dP::::::::88888888888::::::::Yb::::
            ::::dP:::::::::Y888888888P:::::::::Yb::::
          ::::d8:::::::::::Y8888888P:::::::::::8b::::
          .::::88::::::::::::Y88888P::::::::::::88::::.
          :::::Y8baaaaaaaaaa88P:T:Y88aaaaaaaaaad8P:::::
          :::::::Y88888888888P::|::Y88888888888P:::::::
          ::::::::::::::::888:::|:::888::::::::::::::::
          `:::::::::::::::8888888888888b::::::::::::::'
          :::::::::::::::88888888888888::::::::::::::
            :::::::::::::d88888888888888::::::::::::
            ::::::::::::88::88::88:::88::::::::::::
              `::::::::::88::88::88:::88::::::::::'
                `::::::::88::88::P::::88::::::::'
                  `::::::88::88:::::::88::::::'
                    ``:::::::::::::::::::''
                        ``:::::::::''
   */
  resetFakeProgress () {
    this.setState({
      files: progress.reset(this.state.files)
    })
  }

  incFakeProgress () {
    this.setState({
      files: progress.increment(this.state.files)
    })
  }

  completeFakeProgress () {
    this.setState({
      files: progress.complete(this.state.files)
    })
  }

  /**
   * Uploads the selected media
   */
  async handleSave () {
    const anyFileInvalid = any(has('error'))
    if (anyFileInvalid(this.state.files)) return

    const files = this.state.files.filter(file => file.progress !== 100)
    // istanbul ignore next
    if (!files.length) return

    this.resetFakeProgress()

    this.incFakeProgress()
    const interval = setInterval(this.incFakeProgress, updateMs)

    try {
      const mediaMetadata = await upload(
        files,
        this.props.actionType,
        this.props.ownerId
      )

      clearInterval(interval)
      this.completeFakeProgress()

      const ids = pluck('id')

      const newIds = ids(mediaMetadata)
      const currentIds = this.props.value

      const nextIds = union(currentIds, newIds)

      this.props.onChange(nextIds)
    } catch (e) {
      clearInterval(interval)
      notify.error({
        title: 'Failed to upload media',
        message: e.message
      })

      this.resetFakeProgress()
    }
  }

  async handleDrop (newFiles) {
    const filesWithDuration = await Promise.all(addDuration(newFiles))

    const validatedFiles = filesWithDuration.map(file => {
      const error = validateFile(file)
      // istanbul ignore next
      if (error) file.error = error
      return file
    })

    const combineUnique = unionWith(eqBy(prop('name')))
    const files = combineUnique(this.state.files, validatedFiles)

    this.setFiles(files)
  }

  handleRemove (index) {
    const files = remove(index, 1, this.state.files)
    this.setFiles(files)
  }

  setFiles (files) {
    const error = validate({
      isDm: this.props.isDM,
      files,
      actionType: this.props.actionType
    })

    // istanbul ignore next
    this.setState(error ? { error } : { files, error: '' })
  }

  handleHide () {
    this.setState({
      files: [],
      error: ''
    })

    this.props.hideModal()
  }

  render () {
    const { showModal, actionType } = this.props
    const { dropzoneActive, error, files } = this.state
    const props = {
      show: showModal,
      dropzoneActive,
      error,
      files,
      actionType
    }

    return (
      <MediaUploadModal
        {...props}
        onHide={this.handleHide}
        onRemove={this.handleRemove}
        onDrop={this.handleDrop}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        onSave={this.handleSave}
      />
    )
  }
}

MediaUploadModalContainer.propTypes = {
  isDM: PropTypes.bool,
  showModal: PropTypes.bool,
  value: PropTypes.array,
  actionType: PropTypes.string,
  ownerId: PropTypes.string,
  hideModal: PropTypes.func,
  onChange: PropTypes.func
}

const selector = reduxForm.formValueSelector('widget')
const MediaUploadWithActions = connect(
  // istanbul ignore next
  state => ({
    ownerId: selector(state, 'ownerId')
  })
)(MediaUploadModalContainer)

export default MediaUploadWithActions



// WEBPACK FOOTER //
// ./src/pages/widgets/form/media-upload/container.js